import dayjs from 'dayjs'

import CustomParseFormat from 'dayjs/plugin/customParseFormat'
import AdvancedFormat from 'dayjs/plugin/advancedFormat'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import RelativeTime from 'dayjs/plugin/relativeTime'
import UTC from 'dayjs/plugin/utc'
import tz from 'dayjs/plugin/timezone'

dayjs.extend(CustomParseFormat)
dayjs.extend(AdvancedFormat)
dayjs.extend(isBetween)
dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(RelativeTime)
dayjs.extend(UTC)
dayjs.extend(tz)

export const now = () => dayjs().utc()

export const nowTz = (tzVal) => dayjs().tz(tzVal)

export const parseDate = (value) => {
  if (!value) return dayjs('invalid')
  if (typeof value !== 'string') return dayjs(value)
  if (value.endsWith('Z')) return dayjs(value)
  return dayjs(
    value,
    ['YYYY-MM-DD', 'MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm:ssZ'],
    false
  )
}

export const formatDateAs = (value, format) => _format(value, format)
export const formatDate = (value) => _format(value, 'MM/DD/YYYY')
export const formatDateNthDay = (value) => _format(value, 'MMMM Do, YYYY')

export const formatDateTime = (value) => _format(value, 'MM/DD/YYYY hh:mm:ss A')

export const formatIso = (value) => _format(value, 'YYYY-MM-DD')

export const formatTimestamp = (value) => _format(value, 'x')
export const formatDatePretty = (value) => {
  const inputDate = dayjs(value)
  const time = _format(inputDate, 'h:mm A')

  if (isToday(inputDate)) {
    return `Today, ${time}`
  } else if (isTomorrow(inputDate)) {
    return `Tomorrow, ${time}`
  } else if (inputDate.diff(now(), 'day') < 7) {
    return `${_format(inputDate, 'dddd')}, ${time}`
  } else {
    return `${_format(inputDate, 'MMM D')}, ${time}`
  }
}

export const isToday = (value) => parseDate(value).startOf('day').isSame(dayjs().startOf('day'))

export const isTomorrow = (value) =>
  parseDate(value).startOf('day').isSame(dayjs().add(1, 'day').startOf('day'))

export const dateFromString = (str) => {
  switch (str) {
    case 'today':
      return dayjs().startOf('day')
    case 'tomorrow':
      return dayjs().add(1, 'day').startOf('day')
    case 'yesterday':
      return dayjs().add(-1, 'day').startOf('day')
    default:
      return parseDate(str)
  }
}

// When a call-back window starts, in the user's local time. Sent to the backend as the scheduled
// call time, so nextTimeOfDay must stay in step with SaveOutboundContactRequestCommand.NextOccurrenceUtc.
export const callTimes = {
  morning: '08:00',
  afternoon: '12:00',
  evening: '17:00'
}

// The day of the next occurrence of a call-back window's start: today if that time hasn't
// passed yet, otherwise tomorrow.
export const nextTimeOfDay = (timeOfDay, from = dayjs()) => {
  const time = callTimes[timeOfDay]
  if (!time) return dayjs('invalid')

  const [hour, minute] = time.split(':').map(Number)
  const today = dayjs(from).startOf('day')
  return dayjs(from).isAfter(today.hour(hour).minute(minute)) ? today.add(1, 'day') : today
}

// e.g. "October 15th, 2026, Morning"
export const formatNextTimeOfDay = (timeOfDay, from = dayjs()) => {
  const date = nextTimeOfDay(timeOfDay, from)
  if (!date.isValid()) return ''

  return `${date.format('MMMM Do, YYYY')}, ${_capitalize(timeOfDay)}`
}

export const age = (birthDate, asOf = null) => {
  let start = parseInt(_format(birthDate, 'YYYYMMDD'))
  let end = parseInt(_format(asOf || dayjs(), 'YYYYMMDD'))
  return Math.trunc((end - start) / 10000)
}

const _format = (value, format) => {
  if (!value) return ''

  let date = dateFromString(value)
  if (!date.isValid()) return ''

  return date.format(format)
}

export const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
]

export const jan1 = (year) => dayjs.utc(`${year}-01-01`)
